import { ActionTree, MutationTree } from "vuex";
import MaterialApi from "@/api/MaterialApi";
import { IMaterial, ICategory, LocallySearchableAsset, IRootState } from "@iloq/shared";

const materialApi = new MaterialApi();

export interface IMaterialState {
  items: IMaterial[];
  rootCategory?: ICategory;
}

const initialState: IMaterialState = {
  items: [],
  rootCategory: undefined,
};

const filterEmptyCategories = (rootCategory: ICategory, items: IMaterial[]): ICategory => {
  const activeCategories: { [key: string]: boolean } = {};
  items.forEach((item) => {
    item.Categories.forEach((cat) => {
      activeCategories[cat.id] = true;
    });
  });
  const removeInactiveSubcategories = (cat: ICategory): ICategory => {
    if (cat.subcategories) {
      cat.subcategories = cat.subcategories.filter((subCat: ICategory) => {
        const subCatIsActive = activeCategories[subCat.id] === true;
        const processedSubCat = removeInactiveSubcategories(subCat);
        const subCatHasActiveSubCats = (processedSubCat.subcategories || []).length > 0;
        return subCatIsActive || subCatHasActiveSubCats;
      });
    }
    return cat;
  };
  return removeInactiveSubcategories(rootCategory);
};

const actions: ActionTree<IMaterialState, IRootState> = {
  fetchData({ commit, dispatch }): void {
    materialApi
      .getCategories()
      .then((rootCategory: ICategory) => {
        materialApi
          .fetchMaterial()
          .then((items: IMaterial[]) => {
            // Make all the data available at the same moment on purpose
            const filteredRootCategory = filterEmptyCategories(rootCategory, items);
            commit("setRootCategory", filteredRootCategory);
            commit("setItems", items);
            dispatch(
              "globalSearch/refreshData",
              { assetType: LocallySearchableAsset.material, assets: items },
              { root: true },
            );
          })
          .catch((err) => Promise.reject(err));
      })
      .catch((err) => Promise.reject(err));
  },
  async downloadMaterial({ dispatch }, item: IMaterial): Promise<void> {
    const downloadToken = await dispatch("fetchDownloadToken", undefined, { root: true });
    const url = `/api/v1/material/download/asset/${item.id}/${item.AssetName}?auth_token=${downloadToken}`;
    window.open(url, "_blank");
  },
};

const mutations: MutationTree<IMaterialState> = {
  setRootCategory(state: IMaterialState, rootCategory: ICategory): void {
    state.rootCategory = rootCategory;
  },
  setItems(state: IMaterialState, items: IMaterial[]): void {
    const getCategoryPath = (catId: number, catTree: ICategory): ICategory[] => {
      if (catTree.id === catId) {
        return [catTree];
      }
      if (!catTree.subcategories || catTree.subcategories.length === 0) {
        return [];
      }
      let path: ICategory[] = [];
      catTree.subcategories.find((subCat) => {
        const subPath = getCategoryPath(catId, subCat);
        if (subPath.length > 0) {
          path = [catTree, ...subPath];
          return true;
        }
        return false;
      });
      return path;
    };
    items.forEach((item) => {
      const itemCatIds = item.Categories.map((cat) => cat.id);
      item.Categories.forEach((cat) => {
        const path = getCategoryPath(cat.id, state.rootCategory!);
        path.forEach((cat) => {
          if (!itemCatIds.includes(cat.id)) {
            itemCatIds.push(cat.id);
            item.Categories.push({ id: cat.id, name: cat.CategoryName, path: "-" });
          }
        });
      });
    });
    state.items = items;
  },
};

export const materialModule = {
  actions,
  mutations,
  state: initialState,
  namespaced: true,
};
